import React, {createContext, useState, useEffect, useCallback, useMemo} from 'react';
import {List, Map, Set} from 'immutable';

import type {QuestionSetModelV1} from 'resources/augmented/QuestionSet/QuestionSetModel.v1';
import sessionStore from 'client/Session/SessionStore';
import usePageHooks, {PageTypes} from './setPageHooks.react';

import type {DraftAssignmentModelV1 as DraftAssignmentType} from 'resources/GeneratedModels/DraftAssignment/DraftAssignmentModel.v1';
import {DraftAssignmentModelV1} from 'resources/GeneratedModels/DraftAssignment/DraftAssignmentModel.v1';
import {GuideLevelModelV2} from 'resources/augmented/GuideLevel/GuideLevelModel.v2';
import {SubjectModelV2} from 'resources/GeneratedModels/Subject/SubjectModel.v2';

import {genericMandarkRequest, invalidatePartialInterest} from 'resources/mandark.resource';
import {AssignmentModelV3} from 'resources/GeneratedModels/Assignment/AssignmentModel.v3';
import {GuideLevelMapType} from 'client/components/ContentDiscovery/SubjectAndGuideSelector/TopicsModal/GuideLevel.helpers';
import {
  StandardSetObjectType,
  StandardSet
} from 'client/components/ContentDiscovery/StandardSelector/StandardsModal/StandardsModal.types';

import {
  getDraftQuery,
  getDraftQuestionSetsQuery,
  getSearchQuestionsQuery,
  getDraftQuestionsRelationshipQuery
} from 'client/components/ContentDiscovery/ContentDiscovery.queries';
// @TODO: Make question set type not ties to VSS
import {
  QuestionSet,
  QuestionList
} from 'client/Classrooms/Classroom/Teacher/Assignments/ViewStudentSubmission/ViewStudentSubmission.types';
import {addToast} from '@albert-io/atomic';

import {difficultyToNumMap} from 'client/components/PracticeView/PracticeViewToolbar/Filters/filters.utils';

import {
  Difficulties,
  QuestionTypes,
  constructAllOfFilter,
  constructQuestionTypesFilter,
  constructStandardsStandardSetsFilter
} from 'client/components/ContentDiscovery/ContentDiscovery.utils';

type Subject = SubjectModelV2;

// add type guard to isList function
function isList<T>(item: T | List<T>): item is List<T> {
  return List.isList(item as List<T>);
}
// todo - would it make more sense to split this into two providers? One for draft states and one for content discovery query actions?

export interface ContextProps {
  currentPage: PageTypes;
  setCurrentPage: (string: PageTypes) => void;
  handleHashChange: (e) => void; // handler for hashchange event
  questionsQuery: any; // Todo: Need to add a type for queries
  setQuestionsQuery: (any) => void; // todo:  Need to add a type for queries
  selectedQuestionSets: List<QuestionSetModelV1>;
  setSelectedQuestionSets: React.Dispatch<React.SetStateAction<List<QuestionSetModelV1>>>;
  questionDifficulties: List<Difficulties>;
  setQuestionDifficulties: (questionDifficulties: List<Difficulties>) => void;
  questionTypes: List<QuestionTypes>;
  setQuestionTypes: (questionTypes: List<QuestionTypes>) => void;
  hidePreviouslyAssigned: boolean;
  setHidePreviouslyAssigned: (boolean) => void;
  hideAssessmentQuestions: boolean;
  setHideAssessmentQuestions: (boolean) => void;
  searchSort: string | null;
  setSearchSort: (string) => void;
  setDraftSort: (string) => void;
  // filters info
  hasFilters: boolean;
  clearFilters: () => void;
  clearStandardsModal: () => void;
  clearTopicsModal: () => void;
  // loading info
  isLoading: boolean;
  setIsLoading: (boolean) => void;
  // seperating standards info
  filteredStandardSets: Set<StandardSet>;
  setFilteredStandardSets: (filteredStandardSets) => void;
  standardCollection: Map<string, StandardSetObjectType>;
  setStandardCollection: (standardCollection) => void;
  standardModalResults: Map<string, StandardSetObjectType>;
  setStandardModalResults: (standardModalResults) => void;
  standardsSearchString: string;
  setStandardsSearchString: (string) => void;
  hasSelectedStandard: () => boolean;
  getSelectedStandardsAndSets: (standardCollection: Map<string, any>) => {
    selectedStandardIds: string[];
    selectedStandardSetIds: string[];
  };

  // pick for me/add x number of questions
  addXQuestionsVal: number;
  setAddXQuestionsVal: (val: number) => void;

  // ---- Separating topics info ----
  //
  searchString: string;
  setSearchString: (string) => void;
  selectedTopicsModalSubjects: Set<Subject>; // Set of selected subjects in the topics modal subject dropdown
  setSelectedTopicsModalSubjects: (selectedTopicsModalSubjects) => void;
  // this is a map of guide -> guide levels
  // used for keeping track of all selection and click states in topics modal
  guideCollection: Map<string, GuideLevelMapType>;
  setGuideCollection: (guideCollection) => void;

  selectedGuideLevels: List<GuideLevelModelV2>;
  setSelectedGuideLevels: (selectedGuideLevels) => void;
  selectedSubjects: List<SubjectModelV2>; // List of selected subjects in guideCollection
  setSelectedSubjects: (selectedSubjects) => void;

  //
  // Separating draft info
  //
  draftId: string;
  setDraftId: (string) => void;
  draftSort: string | null;
  draftQuestionsQuery: any; // Need to add a type for queries
  setDraftQuestionsQuery: (any) => void; // Need to add a type for queries
  draftAssignment: DraftAssignmentType;
  setDraftAssignment: (DraftAssignmentType) => void;
  addQuestionSetsToDraft: (questionSets: List<QuestionSetModelV1>, name?: string) => void;
  removeQuestionSet: (
    e: any,
    questionSet: QuestionList | QuestionSet | List<QuestionList>,
    onSucces?: () => void
  ) => void;
  draftHasChanges: boolean;
  setDraftHasChanges: (boolean) => void;
  draftHasPendingChanges: boolean;
  questionSetInDraftIds: List<string>;
  draftHasSavedDuringSession: boolean;
  setDraftHasSavedDuringSession: (boolean) => void;
  updateDraftName: (string) => void;
  isQuestionSetSelected: (questionSet: QuestionSetModelV1) => boolean;
  totalQuestionCount: number;
  //
  // assignment info
  //
  assignmentResults: List<AssignmentModelV3>;
  setAssignmentResults: React.Dispatch<React.SetStateAction<List<AssignmentModelV3>>>;

  // draft saving info
  // unsavedDraftAssignment , to differentiate between auto save and not
  // unsavedQuestionSets
  hideSaving: boolean;
  autoSave: boolean;
  setAutoSave: (boolean) => void;
  manualSave: (name: string) => void;
  hasUnsavedChanges: boolean;
  // filter sidebar info
  sidebarCollapsed: boolean;
  setSidebarCollapsed: React.Dispatch<React.SetStateAction<boolean>>;
  draftSaveError: null | any;
  setDraftSaveError: (err: null | any) => void;
  // reset Assignment builder
  resetAssignmentBuilder: () => void;
}

const ContentDiscoveryContext = createContext({} as ContextProps);

interface Props {
  children: React.ReactChild;
}

const ContentDiscoveryProvider = ({children}: Props) => {
  // pick for me/add x number of questions
  const [addXQuestionsVal, setAddXQuestionsVal] = useState(10);
  // filter values
  const [selectedGuideLevels, setSelectedGuideLevels] = useState<List<any>>(List());
  const [selectedSubjects, setSelectedSubjects] = useState<List<string>>(List());
  const [questionDifficulties, setQuestionDifficulties] = useState<List<Difficulties>>(List());
  const [questionTypes, setQuestionTypes] = useState<List<QuestionTypes>>(List());
  const [hidePreviouslyAssigned, setHidePreviouslyAssigned] = useState(false);
  const [hideAssessmentQuestions, setHideAssessmentQuestions] = useState(false);
  const [hasFilters, setHasFilters] = useState(false);
  // sort values
  const [searchSort, setSearchSort] = useState<string | null>(null);
  const [draftSort, setDraftSort] = useState<string | null>(null);
  const [draftAssignment, setDraftAssignment] = useState<DraftAssignmentModelV1 | null>(null);
  const {currentPage, setCurrentPage, resetPageHook, handleHashChange} = usePageHooks();
  const [isLoading, setIsLoading] = useState(false);

  // WIP: Helps know when to re-fetch. May be a better solution on removal of a question
  const [draftHasChanges, setDraftHasChanges] = useState(false); // true if need to refresh
  const [draftHasPendingChanges, setDraftHasPendingChanges] = useState(false); // true when save is in progress
  const [draftHasSavedDuringSession, setDraftHasSavedDuringSession] = useState(false);

  // Helps fetching
  const [draftId, setDraftId] = useState('');

  // These should now be derived from the draft?
  const [selectedQuestionSets, setSelectedQuestionSets] = useState<List<QuestionSet>>(List());

  // Add questions search query
  const [questionsQuery, setQuestionsQuery] = useState<any>(null);
  const [questionCount, setQuestionCount] = useState(0);
  // Draft query
  const [draftQuestionsQuery, setDraftQuestionsQuery] = useState<any>(null);

  // Draft relationship IDs
  const [questionSetInDraftIds, setQuestionSetInDraftIds] = useState<List<string>>(List());

  // topics
  const [searchString, setSearchString] = useState('');
  const [selectedTopicsModalSubjects, setSelectedTopicsModalSubjects] = useState<Set<Subject>>(
    Set()
  );
  const [guideCollection, setGuideCollection] = useState<Map<string, GuideLevelMapType>>(Map());

  // Assignment results
  const [assignmentResults, setAssignmentResults] = useState<List<AssignmentModelV3>>(List());
  // standards
  const [standardsSearchString, setStandardsSearchString] = useState('');
  const [standardCollection, setStandardCollection] = useState<Map<string, any>>(Map());
  const [standardModalResults, setStandardModalResults] = useState<Map<string, any>>(Map());

  const [filteredStandardSets, setFilteredStandardSets] = useState<Set<StandardSet>>(Set());
  // sidebar collapse info
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  // save
  const [autoSave, setAutoSave] = useState(false);
  const [draftSaveError, setDraftSaveError] = useState<null | any>(null);

  const hideSaving = true;

  /*
  #######################
  currentPage query useEffect
  #######################
  */
  useEffect(() => {
    if (currentPage === 'review') {
      setDraftQuestionsQuery(draftId ? getDraftQuestionSetsQuery(draftId) : null);
    } else {
      setQuestionsQuery(questionsQuery);
    }
  }, [currentPage]);

  /*
  #######################
  Start of add questions query useEffects
  #######################
  */

  // updates guideLevels filter
  // Hide assessment questions
  useEffect(() => {
    // @ts-ignore - needs typing
    const guideLevelIds = selectedGuideLevels.reduce((acc, value) => {
      // @ts-ignore - needs typing
      const newList = acc?.push(value.getId());
      return newList;
    }, List());

    const baseQuery = questionsQuery
      ? questionsQuery.unset('filter.guide_levels_v2')
      : getSearchQuestionsQuery();
    const newQuery = baseQuery
      .include('guide_levels_v2.guide_v1')
      .fields({
        guide_v1: 'guide_type'
      })
      .filter(
        {
          guide_levels_v2: {
            guide_v1: {
              guide_type: {
                not: 'assessment'
              }
            }
          }
        },
        hideAssessmentQuestions
      )
      .filter(
        {
          guide_levels_v2: {id: guideLevelIds.join(',')}
        },
        guideLevelIds.size > 0
      );

    setQuestionsQuery(newQuery);
    updateHasFilters();
  }, [selectedGuideLevels, hideAssessmentQuestions]);

  // Handles standards, standard sets, and question types.
  // This is because both of those filters are any_of filters and should be grouped together under an all_of
  // The logic for setting and updating individually was messy
  // for now the cleanest way to do this is remake the entire filter for both at once
  useEffect(() => {
    const baseQuery = questionsQuery
      ? questionsQuery.unset(`filter.all_of`)
      : getSearchQuestionsQuery();

    const {selectedStandardIds, selectedStandardSetIds} =
      getSelectedStandardsAndSets(standardCollection);

    const [standardSetsFilter, standardsFilter] = constructStandardsStandardSetsFilter(
      selectedStandardIds,
      selectedStandardSetIds
    );

    const questionTypesFilter = constructQuestionTypesFilter(questionTypes);

    const allOfFilter = constructAllOfFilter(
      questionTypesFilter,
      standardsFilter,
      standardSetsFilter
    );

    const newQuery = baseQuery.filter(allOfFilter);
    setQuestionsQuery(newQuery);
    updateHasFilters();
  }, [standardCollection, questionTypes]);

  // Updates question difficulty filter
  useEffect(() => {
    const baseQuery = questionsQuery
      ? questionsQuery.unset('filter.questions_v3.difficulty')
      : getSearchQuestionsQuery();
    let newQuery = baseQuery;
    if (!questionDifficulties.isEmpty()) {
      const difficultyValues = questionDifficulties
        .map((val) => {
          if (val) return difficultyToNumMap[val];
          return null;
        })
        .join(',');
      newQuery = baseQuery.filter({
        questions_v3: {
          difficulty: difficultyValues
        }
      });
    }
    setQuestionsQuery(newQuery);
    updateHasFilters();
  }, [questionDifficulties]);

  // Updates previously assigned
  useEffect(() => {
    const baseQuery = questionsQuery
      ? questionsQuery
          .unset(`filter['meta.is_used_in_assignment']`)
          .unset('customQuery.meta.context.assignment_usage')
      : getSearchQuestionsQuery();

    let newQuery = baseQuery;
    if (hidePreviouslyAssigned) {
      newQuery = baseQuery
        .filter({
          'meta.is_used_in_assignment': false
        })
        .meta({
          context: {
            assignment_usage: true
          }
        });
    }
    setQuestionsQuery(newQuery);
    updateHasFilters();
  }, [hidePreviouslyAssigned]);

  // updates search sort on query
  useEffect(() => {
    if (!searchSort) return;
    const baseQuery = questionsQuery
      ? questionsQuery.unset('customQuery.sort')
      : getSearchQuestionsQuery();
    const newQuery = searchSort ? baseQuery.sort(searchSort) : baseQuery;
    setQuestionsQuery(newQuery);
  }, [searchSort]);

  /*
  #######################
  End of add questions query useEffects
  #######################
  */

  /*
  #######################
  Start of draft useEffects
  #######################
  */

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Backend source of truth for our draft info
        const draft = await getDraftQuery(draftId).getResourcePromise();
        setDraftAssignment(draft);
        const relationshipMap = await getDraftQuestionsRelationshipQuery(draftId);
        createRelationshipList(relationshipMap);
        setQuestionCount(draft.getMeta().getCountOfQuestions());
        setDraftHasChanges(false);
      } catch (error) {
        addToast({message: 'Something went wrong getting your draft.', type: 'danger'});
      }
    };

    if (draftId) {
      fetchData();
    }
  }, [draftId, draftHasChanges]);

  // updates draft sort on query
  useEffect(() => {
    if (!draftAssignment) {
      setDraftQuestionsQuery(null);
    } else {
      const baseQuery = draftQuestionsQuery
        ? draftQuestionsQuery.unset('customQuery.sort')
        : getDraftQuestionSetsQuery(draftAssignment.getId());
      const newQuery = draftSort ? baseQuery.sort(draftSort) : baseQuery;
      setDraftQuestionsQuery(newQuery);
    }
  }, [draftSort, draftAssignment]);

  /*
  #######################
  End of draft useEffects
  #######################
  */

  const isQuestionSetSelected = useCallback(
    (questionSet) => {
      if (autoSave) {
        return questionSetInDraftIds.some((id) => id === questionSet.getId());
      }
      return selectedQuestionSets.some(
        (selectedQSet) => selectedQSet!.getId() === questionSet.getId()
      );
    },
    [questionSetInDraftIds, autoSave, selectedQuestionSets]
  );

  const hasSelectedStandard = useCallback(() => {
    return standardCollection.some((standardSetObj) => {
      return standardSetObj.get('isSelected') || standardSetObj.get('isIndeterminate');
    });
  }, [standardCollection]);

  const updateHasFilters = () => {
    if (
      selectedSubjects.isEmpty() &&
      selectedGuideLevels.isEmpty() &&
      questionDifficulties.isEmpty() &&
      questionTypes.isEmpty() &&
      !hasSelectedStandard() &&
      !hidePreviouslyAssigned &&
      !hideAssessmentQuestions
    ) {
      setHasFilters(false);
      setQuestionsQuery(null);
    } else {
      setHasFilters(true);
    }
  };

  const manualSave = (name: string = 'Unnamed draft') => {
    setIsLoading(true);
    setAutoSave(true);
    if (selectedQuestionSets.size > 0) updateDraftQuestionSets(selectedQuestionSets, name);
    else {
      updateDraftName(name);
      setIsLoading(false);
    }
  };

  const totalQuestionCount = useMemo(() => {
    if (autoSave && draftAssignment) {
      return questionCount;
    }
    return selectedQuestionSets.size;
  }, [autoSave, draftAssignment, questionCount, selectedQuestionSets]);

  // Updates the draft model
  const updateDraftQuestionSets = async (sets, name: string = 'Unnamed draft') => {
    // @ts-ignore todo: - Ignoring typing reducer for now
    const questionSetCustomMeta = sets.reduce((acc, questionSet, index) => {
      if (questionSet) {
        // @ts-ignore - acc type
        return acc.set(questionSet.getId(), {position: index});
      }
      return acc;
    }, Map());

    let promiseResponse;
    let relationshipQuestionSetsMap;
    try {
      setQuestionCount((count) => count + List(sets).size);
      setDraftHasPendingChanges(true);
      if (draftAssignment?.existsOnServer()) {
        await draftAssignment?.updateRelationship('question_sets_v1', sets).save();
        relationshipQuestionSetsMap = await getDraftQuestionsRelationshipQuery(draftId);
      } else {
        const draftObject = draftAssignment || new DraftAssignmentModelV1();
        promiseResponse = await draftObject
          .setName(draftAssignment?.getName() || name)
          .updateRelationship('question_sets_v1', sets, true, questionSetCustomMeta)
          .addRelationship({
            type: 'teacher_v1',
            relation: sessionStore.getUserId()
          })
          .save();

        setDraftId(promiseResponse.getId());
        relationshipQuestionSetsMap = await getDraftQuestionsRelationshipQuery(
          promiseResponse.getId()
        );
      }
      createRelationshipList(relationshipQuestionSetsMap);

      // todo: could consolidate to function in provider
      invalidatePartialInterest({
        resourcePath: getDraftQuestionSetsQuery(draftId || promiseResponse.getId()).done()
          .resourcePath
      });
      // needed for invalidating draft list
      invalidatePartialInterest({
        resourcePath: ['draft_assignments_v1']
      });
      setDraftHasChanges(true);
      setDraftHasSavedDuringSession(true);
      setDraftSaveError(null);
    } catch (e) {
      if (draftAssignment) {
        // revert to last saved draft question count on fail
        setQuestionCount(draftAssignment.getMeta().getCountOfQuestions());
      }
      addToast({message: 'something went wrong saving the draft', type: 'danger'});
      setDraftSaveError(e);
    } finally {
      setDraftHasPendingChanges(false);
    }
  };

  // Remove a question set
  const removeQuestionSet = async (
    e,
    questionSet: QuestionSet | QuestionList | List<QuestionList>,
    onSuccess: () => void = () => {}
  ) => {
    e.stopPropagation();
    if (!autoSave) {
      let filteredQuestionSets;
      if (isList<QuestionList | QuestionSet>(questionSet)) {
        const ids = questionSet.toArray().map((qSet) => qSet!.getId());
        // for each question set remove
        filteredQuestionSets = selectedQuestionSets.filter(
          (selectedQSet) => !ids.some((id) => id === (selectedQSet!.getId() as string))
        );
      } else {
        filteredQuestionSets = selectedQuestionSets.filter(
          (item) => item!.getId() !== questionSet.getId()
        );
      }
      setSelectedQuestionSets(List(filteredQuestionSets));
      return;
    }
    let payload;
    let errorMessage = 'An issue occurred trying to remove this question.';
    if (isList<QuestionList | QuestionSet>(questionSet)) {
      payload = {
        data: questionSet.map((question) => ({type: 'question_sets_v1', id: question!.getId()}))
      };
      errorMessage = 'An issue occured try to remove all questions';
    } else {
      payload = {
        data: [
          {
            type: 'question_sets_v1',
            id: questionSet.getId()
          }
        ]
      };
    }

    try {
      setIsLoading(true);
      setDraftHasPendingChanges(true);
      setQuestionCount((count) => count - payload.data.length);
      await genericMandarkRequest(
        'delete',
        {
          resourcePath: [
            'draft_assignments_v1',
            draftAssignment.getId(),
            'relationships',
            'question_sets_v1'
          ]
        },
        payload
      );
      invalidatePartialInterest({
        resourcePath: getDraftQuestionSetsQuery(draftAssignment.getId()).done().resourcePath
      });
      invalidatePartialInterest({
        resourcePath: getDraftQuery(draftAssignment.getId()).done().resourcePath
      });

      onSuccess();

      setDraftQuestionsQuery(getDraftQuestionSetsQuery(draftId));

      setDraftHasSavedDuringSession(true);
      setDraftHasChanges(true);
      setDraftSaveError(null);
    } catch (e) {
      if (draftAssignment) {
        // revert to last saved draft question count on fail
        setQuestionCount(draftAssignment.getMeta().getCountOfQuestions());
      }
      addToast({
        title: 'error',
        message: errorMessage,
        type: 'danger'
      });
      setDraftSaveError(e);
    } finally {
      setIsLoading(false);
      setDraftHasPendingChanges(false);
    }
  };
  const hasUnsavedChanges = useMemo(() => {
    if (draftAssignment) {
      return draftHasPendingChanges;
    }
    // return if question list is long and not autosa ve
    return selectedQuestionSets.size > 0;
  }, [draftAssignment, selectedQuestionSets, draftHasPendingChanges]);

  // Updates the draft model
  const updateDraftName = async (name) => {
    if (!name) return;
    let promiseResponse;
    try {
      setDraftHasPendingChanges(true);
      if (draftAssignment?.existsOnServer()) {
        await draftAssignment?.setName(name).save();
      } else {
        const draftObject = draftAssignment || new DraftAssignmentModelV1();
        promiseResponse = await draftObject
          .setName(name)
          .addRelationship({
            type: 'teacher_v1',
            relation: sessionStore.getUserId()
          })
          .save();

        setDraftId(promiseResponse.getId());
      }

      // todo: could consolidate to function in provider
      invalidatePartialInterest({
        resourcePath: getDraftQuestionSetsQuery(draftId || promiseResponse.getId()).done()
          .resourcePath
      });
      // needed for invalidating draft list
      invalidatePartialInterest({
        resourcePath: ['draft_assignments_v1']
      });
      setDraftHasChanges(true);
      setDraftHasSavedDuringSession(true);
      setDraftSaveError(false);
    } catch (e) {
      addToast({message: 'something went wrong saving the draft', type: 'danger'});
      setDraftSaveError(e);
    } finally {
      setDraftHasPendingChanges(false);
    }
  };

  const clearStandardsModal = () => {
    setStandardsSearchString('');
    setStandardCollection(Map());
    setFilteredStandardSets(Set([]));
    setStandardModalResults(Map());
  };

  const clearTopicsModal = () => {
    setSelectedGuideLevels(List());
    setSearchString('');
    setSelectedTopicsModalSubjects(Set());
    setSelectedSubjects(List());
    setGuideCollection(Map());
  };

  const clearFilters = () => {
    setQuestionDifficulties(List());
    setQuestionTypes(List());
    setHidePreviouslyAssigned(false);
    setHideAssessmentQuestions(false);
    setQuestionsQuery(null);
    clearStandardsModal();
    clearTopicsModal();
  };

  const createRelationshipList = (relationshipQuestionSetsMap) => {
    const relationshipQuestionSetsList = relationshipQuestionSetsMap
      .get('data')
      .reduce((acc, item) => {
        const newList = acc.push(item.get('id'));
        return newList;
      }, List());
    setQuestionSetInDraftIds(relationshipQuestionSetsList);
  };
  // -------------------------
  const resetAssignmentBuilder = () => {
    // filter values
    setSelectedGuideLevels(List());
    setSelectedSubjects(List());
    setQuestionDifficulties(List());
    setQuestionTypes(List());
    setHidePreviouslyAssigned(false);
    setHideAssessmentQuestions(false);
    setHasFilters(false);
    // sort values
    setSearchSort(null);
    setDraftSort(null);
    setDraftAssignment(null);
    resetPageHook();
    setIsLoading(false);
    // WIP: Helps know when to re-fetch. May be a better solution on removal of a question
    setDraftHasChanges(false);
    setDraftHasPendingChanges(false);
    setDraftHasSavedDuringSession(false);
    // Helps fetching
    setDraftId('');
    setSelectedQuestionSets(List());
    // Add questions search query
    setQuestionsQuery(null);
    setQuestionCount(0);
    // Draft query
    setDraftQuestionsQuery(null);
    // topics
    setSearchString('');
    setSelectedTopicsModalSubjects(Set());
    setGuideCollection(Map());
    // Assignment results
    setAssignmentResults(List());
    // standards
    setStandardsSearchString('');
    setStandardCollection(Map());
    setStandardModalResults(Map());
    setFilteredStandardSets(Set());
    // sidebar collapse info
    setSidebarCollapsed(false);
    // save
    setAutoSave(false);
    setDraftSaveError(null);
  };
  // ------------------------------
  const getSelectedStandardsAndSets = (standardCollection) => {
    const selectedStandardSetIds: string[] = [];
    const selectedStandardIds: string[] = [];

    standardCollection.forEach((standardSetObj, standardSetId) => {
      if (!standardSetId) return;

      if (standardSetObj.get('isSelected')) {
        selectedStandardSetIds.push(standardSetId);
      }
      if (standardSetObj.get('isIndeterminate')) {
        // Get selected standard IDs within the set if isIndeterminate is true
        standardSetObj.get('standards').forEach((standardObj, standardId) => {
          if (standardObj.get('isSelected')) {
            selectedStandardIds.push(standardId);
          }
        });
      }
    });
    return {selectedStandardIds, selectedStandardSetIds};
  };

  return (
    <ContentDiscoveryContext.Provider
      value={{
        currentPage,
        setCurrentPage,
        handleHashChange,
        questionsQuery,
        setQuestionsQuery,
        selectedGuideLevels,
        setSelectedGuideLevels,
        selectedSubjects,
        setSelectedSubjects,
        selectedQuestionSets,
        setSelectedQuestionSets,
        addQuestionSetsToDraft: updateDraftQuestionSets,
        removeQuestionSet,
        searchSort,
        setSearchSort,
        hasFilters,
        clearFilters,
        clearStandardsModal,
        clearTopicsModal,
        isLoading,
        setIsLoading,
        questionDifficulties,
        setQuestionDifficulties,
        questionTypes,
        setQuestionTypes,
        hidePreviouslyAssigned,
        setHidePreviouslyAssigned,
        hideAssessmentQuestions,
        setHideAssessmentQuestions,
        // pick for me/add x number of questions
        addXQuestionsVal,
        setAddXQuestionsVal,
        // draft
        draftId,
        setDraftId,
        draftHasChanges,
        setDraftHasChanges,
        draftHasPendingChanges,
        draftQuestionsQuery,
        setDraftQuestionsQuery,
        draftSort,
        setDraftSort,
        draftAssignment,
        setDraftAssignment,
        questionSetInDraftIds,
        draftHasSavedDuringSession,
        setDraftHasSavedDuringSession,
        updateDraftName,
        totalQuestionCount,
        // assignment
        assignmentResults,
        setAssignmentResults,
        isQuestionSetSelected,
        // topics
        searchString,
        setSearchString,
        selectedTopicsModalSubjects,
        setSelectedTopicsModalSubjects,
        guideCollection,
        setGuideCollection,
        // standards
        filteredStandardSets,
        setFilteredStandardSets,
        standardsSearchString,
        setStandardsSearchString,
        standardCollection,
        setStandardCollection,
        setStandardModalResults,
        standardModalResults,
        hasSelectedStandard,
        getSelectedStandardsAndSets,
        // auto save handling
        autoSave,
        setAutoSave,
        manualSave,
        hasUnsavedChanges,
        hideSaving,
        // sidebar collapsed info
        sidebarCollapsed,
        setSidebarCollapsed,
        setDraftSaveError,
        draftSaveError,
        // reset provider
        resetAssignmentBuilder
      }}
    >
      {children}
    </ContentDiscoveryContext.Provider>
  );
};

export {ContentDiscoveryContext, ContentDiscoveryProvider};
